// Integrations — single page with an internal rail that mirrors the 3CX
// secondary nav (CRM, AI, Call Scripts, Google, Amazon, M365, Teams, Hotel
// Services, Transcription, API, Data Connectors). Each section is a sub-route
// under /integrations/*. Reuses existing Stratus editors where they already
// exist (AI Agents, CRM template, API Keys); new sections render configurable
// per-tenant connection panels.

import { useEffect, useState } from 'react';
import { Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Building2, Bot, FileCode2, Cloud, ShoppingCart, Briefcase,
  Users2, Hotel, FileAudio, KeyRound, Database, Save, Eye, EyeOff,
  Plus, Trash2, Download as DownloadIcon, Upload, Copy as CopyIcon,
  BookOpen, MoreVertical, CheckCircle2, AlertCircle, Sparkles,
} from 'lucide-react';
import { getToken, api } from '../lib/api';
import { useToast } from '../components/Toast';
import ApiKeys from './ApiKeys';

const auth = () => ({ Authorization: `Bearer ${getToken()}` });
const fJson = async (url: string, init: RequestInit = {}) => {
  const r = await fetch(url, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...auth(), ...(init.headers || {}) },
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
};

// Shared label-above-input wrapper. Used across the integration panels so the
// per-section markup stays declarative.
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm text-textSecondary mb-1">{label}</label>
      {children}
    </div>
  );
}

// Integration slugs that use the OIDC OAuth flow — Configure opens the start URL
// in a new window rather than calling the configure API endpoint via fetch().
const OIDC_SLUGS: Record<string, string> = { google: 'google', m365: 'm365' };

const SECTIONS = [
  { to: 'crm',             label: 'CRM',              icon: Building2 },
  { to: 'ai',              label: 'AI',               icon: Bot },
  { to: 'copilot',         label: 'Copilot',          icon: Sparkles },
  { to: 'call-scripts',    label: 'Call Scripts',     icon: FileCode2 },
  { to: 'google',          label: 'Google',           icon: Cloud },
  { to: 'amazon',          label: 'Amazon',           icon: ShoppingCart },
  { to: 'm365',            label: 'M365',             icon: Briefcase },
  { to: 'teams',           label: 'Teams',            icon: Users2 },
  { to: 'hotel-services',  label: 'Hotel Services',   icon: Hotel },
  { to: 'transcription',   label: 'Transcription',    icon: FileAudio },
  { to: 'api',             label: 'API',              icon: KeyRound },
  { to: 'data-connectors', label: 'Data Connectors',  icon: Database },
];

export default function Integrations() {
  return (
    <div className="grid grid-cols-[200px_1fr] gap-3">
      {/* Internal rail — fixed-width column */}
      <nav className="card p-1 self-start">
        {SECTIONS.map(s => {
          const Icon = s.icon;
          return (
            <NavLink
              key={s.to}
              to={`/integrations/${s.to}`}
              end
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 text-sm rounded-md ${
                  isActive ? 'bg-elevated text-primary' : 'hover:bg-elevated'
                }`
              }
            >
              <Icon size={14} /> {s.label}
            </NavLink>
          );
        })}
      </nav>

      <div className="min-w-0">
        <Routes>
          <Route index                        element={<Navigate to="/integrations/crm" replace />} />
          <Route path="crm"                   element={<CrmIntegration />} />
          <Route path="ai"                    element={<AiPanel />} />
          <Route path="copilot"               element={<CopilotPanel />} />
          <Route path="call-scripts"          element={<CallScripts />} />
          <Route path="google"                element={<GooglePanel />} />
          <Route path="amazon"                element={<AmazonPanel />} />
          <Route path="m365"                  element={<M365Panel />} />
          <Route path="teams"                 element={<TeamsPanel />} />
          <Route path="hotel-services"        element={<HotelServicesPanel />} />
          <Route path="transcription"         element={<TranscriptionPanel />} />
          <Route path="api"                   element={<ApiKeys />} />
          <Route path="data-connectors"       element={<DataConnectorsPanel />} />
          <Route path="*"                     element={<Navigate to="/integrations/crm" replace />} />
        </Routes>
      </div>
    </div>
  );
}

// ─────────────────────────────────────── CRM Integration ──────────────────────

const CRM_SOLUTIONS = [
  'AgentDesk', 'HubSpot', 'Salesforce', 'Zoho', 'Pipedrive', 'Microsoft Dynamics',
  'Freshdesk', 'Zendesk', 'Bullhorn', 'Vtiger', 'SuiteCRM', 'Custom',
];

function CrmIntegration() {
  const qc = useQueryClient();
  const toast = useToast();
  const cfg = useQuery<any>({ queryKey: ['integrations-crm'], queryFn: () => fJson('/api/integrations/crm') });

  const [solution, setSolution] = useState('AgentDesk');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw,   setShowPw]   = useState(false);
  const [queryWhen,    setQueryWhen]    = useState<'always' | 'no_match' | 'never'>('always');
  const [addToPhonebook, setAddToPhonebook] = useState(false);

  useEffect(() => {
    const c = cfg.data;
    if (!c) return;
    setSolution(c.solution || 'AgentDesk');
    setUsername(c.username || '');
    setPassword(c.password || '');
    setQueryWhen(c.query_when || 'always');
    setAddToPhonebook(!!c.add_to_phonebook);
  }, [cfg.data]);

  const save = useMutation({
    mutationFn: () => fJson('/api/integrations/crm', {
      method: 'PUT',
      body: JSON.stringify({
        solution, username, password,
        query_when: queryWhen,
        add_to_phonebook: addToPhonebook,
      }),
    }),
    onSuccess: () => { toast('success', 'Saved'); qc.invalidateQueries({ queryKey: ['integrations-crm'] }); },
    onError:   (e: any) => toast('error', String(e.message || e)),
  });

  const test = useMutation({
    mutationFn: () => fJson('/api/integrations/crm/test', { method: 'POST' }),
    onSuccess: (r: any) => toast(r?.ok ? 'success' : 'error', r?.message || (r?.ok ? 'Connection OK' : 'Connection failed')),
    onError:   (e: any) => toast('error', String(e.message || e)),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold flex-1">CRM Integration</h1>
        <button className="btn-primary text-sm inline-flex items-center gap-1" disabled={save.isPending} onClick={() => save.mutate()}>
          <Save size={14} /> Save
        </button>
        <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={() => test.mutate()}>
          TEST
        </button>
      </div>

      <div className="card p-4 space-y-3">
        <div>
          <label className="block text-sm text-textSecondary mb-1">Select a CRM Solution</label>
          <div className="flex gap-2">
            <select className="input flex-1" value={solution} onChange={e => setSolution(e.target.value)}>
              {CRM_SOLUTIONS.map(s => <option key={s}>{s}</option>)}
            </select>
            <button className="btn-primary text-sm" onClick={() => location.assign('/crm-template')}>+ Add Template</button>
            <button className="btn-primary text-sm" onClick={() => location.assign('/crm-writeback')}>Call Journaling</button>
            <button className="btn-primary text-sm" onClick={() => location.assign('/crm-template')}>Delete</button>
            <button className="btn-primary text-sm" onClick={() => location.assign('/crm-template')}>Show Template</button>
          </div>
        </div>

        <div>
          <label className="block text-sm text-textSecondary mb-1">Username:</label>
          <input className="input" value={username} onChange={e => setUsername(e.target.value)} />
        </div>

        <div>
          <label className="block text-sm text-textSecondary mb-1">Password:</label>
          <div className="relative">
            <input className="input pr-10" type={showPw ? 'text' : 'password'} value={password}
                   onChange={e => setPassword(e.target.value)} />
            <button className="absolute right-2 top-1/2 -translate-y-1/2 text-textSecondary"
                    onClick={() => setShowPw(s => !s)} title={showPw ? 'Hide' : 'Show'}>
              {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm text-textSecondary mb-1">Query CRM</label>
          <select className="input" value={queryWhen} onChange={e => setQueryWhen(e.target.value as any)}>
            <option value="always">Always query</option>
            <option value="no_match">Only if no Stratus contact matches</option>
            <option value="never">Never</option>
          </select>
        </div>

        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={addToPhonebook} onChange={e => setAddToPhonebook(e.target.checked)} />
          Add CRM contacts to Stratus company phonebook when a call or message is sent or received
        </label>
      </div>
    </div>
  );
}

// ─────────────────────────────────────── Twilio (creds + Connect & Verify) ────

// ─────────────────────────────────────── Copilot (assistant model settings) ──
function CopilotPanel() {
  const qc = useQueryClient();
  const toast = useToast();
  const cfg = useQuery<any>({ queryKey: ['copilot-config'], queryFn: () => fJson('/api/copilot/config') });

  const [enabled, setEnabled]     = useState(true);
  const [baseUrl, setBaseUrl]     = useState('');
  const [model, setModel]         = useState('');
  const [temperature, setTemp]    = useState(0.2);
  const [maxTokens, setMaxTokens] = useState(1024);
  const [apiKey, setApiKey]       = useState('');
  const [showKey, setShowKey]     = useState(false);
  const [testing, setTesting]     = useState(false);
  const [testOut, setTestOut]     = useState<string>('');

  const c = cfg.data;
  useEffect(() => {
    if (!c) return;
    setEnabled(c.enabled !== false);
    setBaseUrl(c.base_url || '');
    setModel(c.completion_model || '');
    setTemp(typeof c.temperature === 'number' ? c.temperature : 0.2);
    setMaxTokens(typeof c.max_tokens === 'number' ? c.max_tokens : 1024);
    setApiKey('');
  }, [c]);

  function buildBody() {
    const body: any = {
      enabled,
      base_url: baseUrl.trim(),
      completion_model: model.trim(),
      temperature: Number(temperature),
      max_tokens: Number(maxTokens),
    };
    if (apiKey.trim()) body.api_key = apiKey.trim();
    return body;
  }

  const save = useMutation({
    mutationFn: () => fJson('/api/copilot/config', { method: 'PUT', body: JSON.stringify(buildBody()) }),
    onSuccess: () => { toast('success', 'Saved'); qc.invalidateQueries({ queryKey: ['copilot-config'] }); },
    onError:   (e: any) => toast('error', String(e.message || e)),
  });

  // Test connectivity by asking the live endpoint a trivial question.
  async function runTest() {
    setTesting(true); setTestOut('');
    const t0 = Date.now();
    try {
      await fJson('/api/copilot/config', { method: 'PUT', body: JSON.stringify(buildBody()) });
      const r = await fJson('/api/copilot/chat', { method: 'POST', body: JSON.stringify({ message: 'Reply with just the word: ok' }) });
      setTestOut(`OK (${((Date.now() - t0) / 1000).toFixed(1)}s, ${r.model || 'model'}): ${String(r.answer || '').slice(0, 80)}`);
      qc.invalidateQueries({ queryKey: ['copilot-config'] });
    } catch (e: any) { setTestOut('Failed: ' + String(e.message || e)); }
    finally { setTesting(false); }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold flex-1">Copilot</h1>
        <button className="btn-ghost text-sm inline-flex items-center gap-1" disabled={testing} onClick={runTest}>
          {testing ? 'Testing…' : <><CheckCircle2 size={14} /> Test</>}
        </button>
        <button className="btn-primary text-sm inline-flex items-center gap-1" disabled={save.isPending} onClick={() => save.mutate()}>
          <Save size={14} /> Save
        </button>
      </div>

      {testOut && (
        <div className={`card p-3 text-sm ${/^OK/.test(testOut) ? 'text-success' : 'text-danger'}`}>{testOut}</div>
      )}

      <div className="card p-4 space-y-4">
        <p className="text-sm text-textSecondary">
          The Copilot is the in-console assistant (the orb, bottom-right) that answers setup
          questions and diagnoses live call issues using this deployment's own data. It runs on
          the on-prem GLM endpoint by default — change the endpoint/model here only if you host
          the model elsewhere. Twilio call analytics turn on automatically once you connect Twilio
          in the <strong>Twilio</strong> tab.
        </p>

        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
          Enable the Copilot assistant
        </label>

        <Field label="Model endpoint (OpenAI-compatible /v1 base URL)">
          <input className="input font-mono" value={baseUrl} onChange={e => setBaseUrl(e.target.value)}
                 placeholder="http://192.168.2.55:8000/v1" />
        </Field>
        <Field label="Model name">
          <input className="input font-mono" value={model} onChange={e => setModel(e.target.value)}
                 placeholder="glm-4.6v" />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Temperature">
            <input className="input" type="number" step="0.1" min={0} max={1} value={temperature}
                   onChange={e => setTemp(Number(e.target.value))} />
          </Field>
          <Field label="Max tokens">
            <input className="input" type="number" min={128} max={8192} value={maxTokens}
                   onChange={e => setMaxTokens(Number(e.target.value))} />
          </Field>
        </div>

        <Field label="API key (only for a cloud endpoint that requires one — optional)">
          <div className="relative">
            <input className="input pr-10 font-mono" type={showKey ? 'text' : 'password'}
                   value={apiKey} onChange={e => setApiKey(e.target.value)}
                   placeholder={c?.api_key_set ? '•••••••• (stored — leave blank to keep)' : 'not required for on-prem GLM'} />
            <button className="absolute right-2 top-1/2 -translate-y-1/2 text-textSecondary"
                    onClick={() => setShowKey(s => !s)} title={showKey ? 'Hide' : 'Show'}>
              {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
        </Field>

        <div className={`text-sm inline-flex items-center gap-2 ${c?.twilio_configured ? 'text-success' : 'text-textSecondary'}`}>
          {c?.twilio_configured ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
          Twilio call analytics: {c?.twilio_configured
            ? 'available (Twilio connected)'
            : 'off — connect Twilio under Voice & Chat → Twilio to enable carrier-side call debugging.'}
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────── AI Agents (3CX-shape table) ─────────

function AiPanel() {
  const qc = useQueryClient();
  const toast = useToast();
  const list   = useQuery<any[]>({ queryKey: ['ai-agents-flat'], queryFn: () => fJson('/api/ai-agents') });
  const users  = useQuery<any[]>({ queryKey: ['users-list'],     queryFn: () => fJson('/api/users') });
  const tenants = useQuery<any[]>({ queryKey: ['tenants'],       queryFn: () => fJson('/api/tenants') });
  const [search, setSearch]   = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [addOpen, setAddOpen]   = useState(false);

  // 3CX shows one row per AI agent with the role baked in. We compose rows
  // from /api/ai-agents (config) + /api/users (display names + extension).
  const userByExtUuid = new Map((users.data || []).map((u: any) => [u.extension_uuid, u]));
  const deptByUuid    = new Map((tenants.data || []).map((t: any) => [t.domain_uuid, t.domain_description || t.domain_name || '']));

  const rows = (list.data || [])
    .filter((a: any) => a.enabled !== false)
    .map((a: any) => {
      const u = userByExtUuid.get(a.extension_uuid);
      const role = a.role || (a.system_prompt ? 'Custom Receptionist' : 'Receptionist');
      return {
        agent_id:   a.extension_uuid,
        name:       u ? `${u.last_name || ''}${u.last_name ? ', ' : ''}${u.first_name || u.name || u.username || ''}` : (a.agent_name || 'Agent'),
        role,
        extension:  u?.extension || a.extension || '',
        department: deptByUuid.get(u?.domain_uuid || a.domain_uuid) || '',
        dids:       Array.isArray(a.dids) ? a.dids.join(', ') : (a.dids || ''),
        online:     a.enabled !== false,
      };
    })
    .filter((r: any) => !search || `${r.name} ${r.extension} ${r.department}`.toLowerCase().includes(search.toLowerCase()));

  function toggle(id: string) {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  }
  function toggleAll() {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((r: any) => r.agent_id)));
  }

  const remove = useMutation({
    mutationFn: (ids: string[]) => Promise.all(ids.map(id => fJson(`/api/ai-agents/${id}`, { method: 'DELETE' }))),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ai-agents-flat'] }); setSelected(new Set()); toast('success', 'Deleted'); },
    onError:   (e: any) => toast('error', String(e.message || e)),
  });

  function exportJson() {
    const blob = new Blob([JSON.stringify(list.data || [], null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `ai-agents-${new Date().toISOString().slice(0,10)}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  }
  function importJson() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json,.json';
    input.onchange = async () => {
      const f = input.files?.[0];
      if (!f) return;
      try {
        const items = JSON.parse(await f.text());
        if (!Array.isArray(items)) throw new Error('Expected an array');
        await fJson('/api/ai-agents/import', { method: 'POST', body: JSON.stringify({ agents: items }) });
        qc.invalidateQueries({ queryKey: ['ai-agents-flat'] });
        toast('success', `Imported ${items.length} agent(s)`);
      } catch (e: any) { toast('error', String(e.message || e)); }
    };
    input.click();
  }
  async function copySelected() {
    if (!selected.size) return toast('error', 'Select rows to copy');
    try {
      await fJson('/api/ai-agents/copy', { method: 'POST', body: JSON.stringify({ ids: [...selected] }) });
      qc.invalidateQueries({ queryKey: ['ai-agents-flat'] });
      toast('success', `Copied ${selected.size} agent(s)`);
    } catch (e: any) { toast('error', String(e.message || e)); }
  }

  return (
    <div className="space-y-3">
      <h1 className="text-xl font-semibold">AI Agents</h1>

      {/* Toolbar — exact 3CX shape */}
      <div className="card p-2 flex flex-wrap gap-1 items-center">
        <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={() => location.assign('/integrations/transcription')}>
          <Bot size={14} /> OpenAI API
        </button>
        <button disabled title="Coming soon" className="btn-primary text-sm inline-flex items-center gap-1 opacity-50 cursor-not-allowed">
          <BookOpen size={14} /> Knowledge Sources
        </button>
        <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={() => setAddOpen(o => !o)}>
          <Plus size={14} /> Add Agent ▾
        </button>
        <button className="btn-primary text-sm inline-flex items-center gap-1"
                onClick={() => {
                  if (!selected.size) return toast('error', 'Select rows to delete');
                  if (confirm(`Delete ${selected.size} agent(s)?`)) remove.mutate([...selected]);
                }}>
          <Trash2 size={14} /> Delete
        </button>
        <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={importJson}>
          <Upload size={14} /> Import
        </button>
        <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={exportJson}>
          <DownloadIcon size={14} /> Export ({(list.data || []).length})
        </button>
        <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={copySelected}>
          <CopyIcon size={14} /> Copy
        </button>
      </div>

      {/* Search */}
      <div className="flex items-center gap-1 bg-elevated rounded-md px-2 py-1.5">
        <input className="bg-transparent outline-none text-sm w-full"
               value={search} onChange={e => setSearch(e.target.value)}
               placeholder="Search…" />
      </div>

      {/* Add menu (simple — opens existing per-extension editor on /ai-agents) */}
      {addOpen && (
        <div className="card p-3 inline-flex flex-col gap-1">
          {['Receptionist', 'Custom Receptionist', 'Outbound Caller', 'Voicemail-AI'].map(kind => (
            <button key={kind} className="text-left text-sm px-2 py-1.5 rounded hover:bg-elevated"
                    onClick={() => { setAddOpen(false); location.assign(`/ai-agents?new=${encodeURIComponent(kind)}`); }}>
              {kind}
            </button>
          ))}
        </div>
      )}

      {/* Table */}
      <div className="card p-0 overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              <th className="w-8">
                <input type="checkbox"
                       checked={rows.length > 0 && selected.size === rows.length}
                       onChange={toggleAll} />
              </th>
              <th>AI Agent</th>
              <th className="w-28">Extension</th>
              <th className="w-48">Department</th>
              <th>DIDs</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r: any) => (
              <tr key={r.agent_id} className="hover:bg-elevated cursor-pointer"
                  onClick={() => location.assign(`/ai-agents?ext=${r.agent_id}`)}>
                <td onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={selected.has(r.agent_id)} onChange={() => toggle(r.agent_id)} />
                </td>
                <td>
                  <span className="inline-flex items-center gap-2 text-sm">
                    <span className={`inline-block w-2.5 h-2.5 rounded-sm ${r.online ? 'bg-success' : 'bg-muted/60'}`} />
                    <span className="font-medium">{r.name}</span>
                    <span className="text-xs text-textSecondary">({r.role})</span>
                  </span>
                </td>
                <td className="font-mono text-sm">{r.extension}</td>
                <td className="text-sm">{r.department}</td>
                <td className="text-sm">{r.dids}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  <button disabled title="Coming soon" className="btn-ghost p-1 opacity-40 cursor-not-allowed"><MoreVertical size={14} /></button>
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={6} className="text-center py-8 text-textSecondary">No AI agents.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────── Call Scripts ─────────────────────────

function CallScripts() {
  const qc = useQueryClient();
  const list = useQuery<any[]>({ queryKey: ['call-scripts'], queryFn: () => fJson('/api/integrations/call-scripts') });
  const [search, setSearch] = useState('');

  const remove = useMutation({
    mutationFn: (id: string) => fJson(`/api/integrations/call-scripts/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['call-scripts'] }),
  });

  const rows = (list.data || []).filter((r: any) =>
    !search || JSON.stringify(r).toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-3">
      <h1 className="text-xl font-semibold">Call Processing Script</h1>
      <div className="card p-2 flex gap-1 items-center">
        <button disabled title="Coming soon — script marketplace" className="btn-primary text-sm opacity-50 cursor-not-allowed">+ Add from Store</button>
        <button disabled title="Coming soon — custom script editor" className="btn-primary text-sm opacity-50 cursor-not-allowed">+ Add Custom</button>
        <button className="btn-primary text-sm" onClick={() => {
          const ids = rows.filter((r: any) => r._selected).map((r: any) => r.script_id);
          if (!ids.length) return alert('Select rows to delete.');
          if (confirm(`Delete ${ids.length} script(s)?`)) ids.forEach((id: string) => remove.mutate(id));
        }}>Delete</button>
        <div className="flex-1" />
        <input className="input w-64" placeholder="Search …" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <div className="card p-0 overflow-hidden">
        <table className="table">
          <thead>
            <tr>
              <th className="w-8"></th>
              <th>Extension</th>
              <th>Department</th>
              <th>Run this script</th>
              <th>Last success time</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r: any) => (
              <tr key={r.script_id} className="hover:bg-elevated">
                <td><input type="checkbox" /></td>
                <td className="font-mono">{r.extension}</td>
                <td>{r.department || ''}</td>
                <td>{r.script_name}</td>
                <td className="text-xs">{r.last_success_at ? new Date(r.last_success_at).toLocaleString() : '—'}</td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={5} className="text-center py-8 text-textSecondary">No call processing scripts.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────── Generic provider panel ───────────────

function ProviderPanel({
  title, slug, intro, sections,
}: {
  title: string;
  slug: string;
  intro: string;
  sections: { name: string; description: string; configKey: string }[];
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const cfg = useQuery<any>({
    queryKey: ['integrations', slug],
    queryFn: () => fJson(`/api/integrations/${slug}`),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold flex-1">{title}</h1>
        <button disabled title="Use the per-section Configure buttons below" className="btn-primary text-sm inline-flex items-center gap-1 opacity-50 cursor-not-allowed">
          <Save size={14} /> Save
        </button>
      </div>

      {sections.map(s => {
        const isConfigured = !!cfg.data?.[s.configKey];
        return (
          <div key={s.name} className="card p-4 space-y-3">
            <h2 className="font-semibold">{s.name}</h2>
            <p className="text-sm text-textSecondary">
              {isConfigured ? `${s.name} is configured.` : `${s.name} is not configured. ${s.description}`}
            </p>
            <button
              className="btn-primary text-sm inline-flex items-center gap-1"
              onClick={() => {
                // OAuth providers: open the OIDC start URL in a new window so the browser
                // can follow the consent redirect chain (fetch() can't do this).
                const oidcProvider = OIDC_SLUGS[slug];
                const domainUuid = api.me()?.domain_uuid;
                if (oidcProvider && domainUuid) {
                  window.open(
                    `/api/auth/oidc/${oidcProvider}/start?domain_uuid=${encodeURIComponent(domainUuid)}`,
                    '_blank',
                  );
                  return;
                }
                fJson(`/api/integrations/${slug}/${s.configKey}/configure`, { method: 'POST' })
                  .then(() => {
                    toast('success', `${s.name} configuration started`);
                    qc.invalidateQueries({ queryKey: ['integrations', slug] });
                  })
                  .catch((e: any) => toast('error', String(e.message || e)));
              }}
            >
              <Save size={14} /> Configure
            </button>
          </div>
        );
      })}

      <div className="text-xs text-textSecondary">
        {intro}
      </div>
    </div>
  );
}

function GooglePanel() {
  return (
    <ProviderPanel
      title="Google Workspace"
      slug="google"
      intro="Configure to sync Google contacts, schedule events, and enable Google sign-in."
      sections={[
        { name: 'Google Workspace',     configKey: 'workspace', description: 'Follow the steps below in order to create your App.' },
        { name: 'Google SSO Integration', configKey: 'sso',     description: 'Press the button below to start.' },
      ]}
    />
  );
}

function M365Panel() {
  return (
    <ProviderPanel
      title="M365"
      slug="m365"
      intro="Configure Microsoft 365 to sync calendars, contacts, and presence."
      sections={[
        { name: 'Microsoft 365', configKey: 'tenant', description: 'Follow the steps below in order to create your App.' },
      ]}
    />
  );
}

// ─────────────────────────────────────── Amazon (AWS) ────────────────────────

function AmazonPanel() {
  const qc = useQueryClient();
  const toast = useToast();
  const cfg = useQuery<any>({ queryKey: ['integrations-amazon'], queryFn: () => fJson('/api/integrations/amazon') });

  const [accessKeyId,     setAccessKeyId]     = useState('');
  const [secretAccessKey, setSecretAccessKey] = useState('');
  const [showSecret,      setShowSecret]      = useState(false);
  const [bucketName,      setBucketName]      = useState('');
  const [region,          setRegion]          = useState('');
  const [applyRemote,     setApplyRemote]     = useState(false);

  useEffect(() => {
    const c = cfg.data;
    if (!c) return;
    setAccessKeyId(c.access_key_id || '');
    setSecretAccessKey(c.secret_access_key || '');
    setBucketName(c.bucket_name || '');
    setRegion(c.region || '');
    setApplyRemote(!!c.apply_remote_storage);
  }, [cfg.data]);

  const save = useMutation({
    mutationFn: () => fJson('/api/integrations/amazon', {
      method: 'PUT',
      body: JSON.stringify({
        access_key_id: accessKeyId, secret_access_key: secretAccessKey,
        bucket_name: bucketName, region,
        apply_remote_storage: applyRemote,
      }),
    }),
    onSuccess: () => { toast('success', 'Saved'); qc.invalidateQueries({ queryKey: ['integrations-amazon'] }); },
    onError:   (e: any) => toast('error', String(e.message || e)),
  });

  function downloadIamPolicy() {
    const policy = {
      Version: '2012-10-17',
      Statement: [{
        Effect: 'Allow',
        Action: ['s3:PutObject', 's3:GetObject', 's3:DeleteObject', 's3:ListBucket'],
        Resource: [
          bucketName ? `arn:aws:s3:::${bucketName}` : 'arn:aws:s3:::YOUR-BUCKET',
          bucketName ? `arn:aws:s3:::${bucketName}/*` : 'arn:aws:s3:::YOUR-BUCKET/*',
        ],
      }],
    };
    const blob = new Blob([JSON.stringify(policy, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'stratus-iam-policy.json';
    a.click();
    URL.revokeObjectURL(a.href);
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold flex-1">Amazon Services</h1>
        <button className="btn-primary text-sm inline-flex items-center gap-1" disabled={save.isPending} onClick={() => save.mutate()}>
          <Save size={14} /> Save
        </button>
      </div>

      <div className="card p-4 space-y-4">
        <p className="text-sm">
          Prepare your AWS Account as you'll need permissions to create IAM Policies, IAM Users, and S3 Buckets in your AWS Console.
          View Step-by-Step <a className="text-primary underline" href="https://docs.aws.amazon.com/IAM/latest/UserGuide/getting-started.html" target="_blank" rel="noreferrer">guide</a>{' '}
          and Login to your <a className="text-primary underline" href="https://aws.amazon.com/console/" target="_blank" rel="noreferrer">AWS Console</a>.
        </p>

        <div>
          <h2 className="font-semibold mb-1">Generate IAM Policy &amp; User Instructions</h2>
          <p className="text-sm text-textSecondary mb-2">
            This policy defines the minimal permissions our service needs to interact with your S3 bucket.
            This config file will be needed to apply this in AWS.
          </p>
          <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={downloadIamPolicy}>
            <Save size={14} /> Generate IAM Config File
          </button>
        </div>

        <div className="space-y-2">
          <h2 className="font-semibold">Input AWS Access Keys</h2>
          <p className="text-sm text-textSecondary">
            Once you've created your IAM User and obtained the keys in AWS, paste them below.
          </p>
          <div>
            <label className="block text-sm text-textSecondary mb-1">Access Key ID</label>
            <input className="input" value={accessKeyId} onChange={e => setAccessKeyId(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm text-textSecondary mb-1">Secret Access Key</label>
            <div className="relative">
              <input className="input pr-10" type={showSecret ? 'text' : 'password'} value={secretAccessKey}
                     onChange={e => setSecretAccessKey(e.target.value)} />
              <button className="absolute right-2 top-1/2 -translate-y-1/2 text-textSecondary"
                      onClick={() => setShowSecret(s => !s)} title={showSecret ? 'Hide' : 'Show'}>
                {showSecret ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <h2 className="font-semibold">Configure S3 Bucket Details</h2>
          <p className="text-sm text-textSecondary">Provide the name of your S3 bucket and the AWS Region it resides in.</p>
          <div>
            <label className="block text-sm text-textSecondary mb-1">Bucket Name</label>
            <input className="input" value={bucketName} onChange={e => setBucketName(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm text-textSecondary mb-1">Region</label>
            <input className="input" value={region} onChange={e => setRegion(e.target.value)} placeholder="us-east-1, eu-central-1, ap-northeast-3" />
            <div className="text-xs text-textSecondary mt-1">e.g. us-east-1, eu-central-1, ap-northeast-3</div>
          </div>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={applyRemote} onChange={e => setApplyRemote(e.target.checked)} />
            Apply this bucket for remote storage
          </label>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────── Teams Direct Routing ────────────────

const COUNTRY_CODES = [
  'United States', 'Canada', 'United Kingdom', 'Australia', 'Germany', 'France',
  'Spain', 'Italy', 'Netherlands', 'Belgium', 'Sweden', 'Norway', 'Finland',
  'Denmark', 'Ireland', 'New Zealand', 'South Africa', 'Mexico', 'Brazil',
];

function TeamsPanel() {
  const qc = useQueryClient();
  const toast = useToast();
  const cfg = useQuery<any>({ queryKey: ['integrations-teams'], queryFn: () => fJson('/api/integrations/teams') });

  const [enabled,    setEnabled]    = useState(false);
  const [fqdn,       setFqdn]       = useState('');
  const [certificate, setCertificate] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const [country,    setCountry]    = useState('');
  const [areaCode,   setAreaCode]   = useState('');

  useEffect(() => {
    const c = cfg.data;
    if (!c) return;
    setEnabled(!!c.enabled);
    setFqdn(c.fqdn || '');
    setCertificate(c.certificate || '');
    setPrivateKey(c.private_key || '');
    setCountry(c.country || '');
    setAreaCode(c.area_code || '');
  }, [cfg.data]);

  const save = useMutation({
    mutationFn: () => fJson('/api/integrations/teams', {
      method: 'PUT',
      body: JSON.stringify({
        enabled, fqdn,
        certificate, private_key: privateKey,
        country, area_code: areaCode,
      }),
    }),
    onSuccess: () => { toast('success', 'Saved'); qc.invalidateQueries({ queryKey: ['integrations-teams'] }); },
    onError:   (e: any) => toast('error', String(e.message || e)),
  });

  async function readFile(setter: (s: string) => void) {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pem,.crt,.cer,.key,.txt,text/plain';
    input.onchange = async () => {
      const f = input.files?.[0];
      if (!f) return;
      setter(await f.text());
    };
    input.click();
  }

  function downloadDialPlanScript() {
    const script = `# Teams Direct Routing dial plan for ${country || 'unknown'} ${areaCode || ''}\n` +
      `# Apply this PowerShell to MS Teams admin\n` +
      `New-CsTenantDialPlan -Identity Stratus-${areaCode || 'AreaCode'}\n` +
      `# (extend with your dial-plan rules)\n`;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([script], { type: 'text/plain' }));
    a.download = 'teams-dial-plan.ps1';
    a.click();
    URL.revokeObjectURL(a.href);
  }

  function downloadUsersScript() {
    const script = `# Teams users provisioning — run after every user add/remove\n` +
      `# Sets OnlineVoiceRoutingPolicy and TenantDialPlan for each licensed user.\n` +
      `Connect-MicrosoftTeams\n` +
      `Get-CsOnlineUser | Grant-CsOnlineVoiceRoutingPolicy -PolicyName Stratus-${areaCode || 'AreaCode'}\n`;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([script], { type: 'text/plain' }));
    a.download = 'teams-users.ps1';
    a.click();
    URL.revokeObjectURL(a.href);
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold flex-1">Teams Direct Routing</h1>
        <button className="btn-primary text-sm inline-flex items-center gap-1" disabled={save.isPending} onClick={() => save.mutate()}>
          <Save size={14} /> Save
        </button>
      </div>

      <div className="card p-4 space-y-4">
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
          Enable Microsoft Teams Direct Routing
        </label>

        <div>
          <h2 className="font-semibold mb-1">Step 1: Specify your Teams FQDN</h2>
          <input className="input" value={fqdn} onChange={e => setFqdn(e.target.value)}
                 placeholder="Your Teams SBC FQDN should match the domain email of MS365 - for example teams.contoso.com" />
        </div>

        <div>
          <h2 className="font-semibold mb-1">Step 2: Create a certificate and key for this FQDN</h2>
          <p className="text-sm text-textSecondary mb-2">
            Microsoft requires a certificate from one of the following <a className="text-primary underline" href="https://learn.microsoft.com/microsoftteams/direct-routing-plan" target="_blank" rel="noreferrer">providers</a>.
          </p>
          <div className="space-y-2">
            <div>
              <label className="block text-sm text-textSecondary mb-1">Certificate</label>
              <div className="flex gap-1">
                <textarea className="input font-mono text-xs flex-1" rows={3} value={certificate}
                          onChange={e => setCertificate(e.target.value)} placeholder="-----BEGIN CERTIFICATE-----…" />
                <button className="btn-primary text-sm" onClick={() => readFile(setCertificate)} title="Upload">↑</button>
                <button className="btn-ghost text-sm" onClick={() => setCertificate('')} title="Clear">×</button>
              </div>
            </div>
            <div>
              <label className="block text-sm text-textSecondary mb-1">Private Key</label>
              <div className="flex gap-1">
                <textarea className="input font-mono text-xs flex-1" rows={3} value={privateKey}
                          onChange={e => setPrivateKey(e.target.value)} placeholder="-----BEGIN PRIVATE KEY-----…" />
                <button className="btn-primary text-sm" onClick={() => readFile(setPrivateKey)} title="Upload">↑</button>
                <button className="btn-ghost text-sm" onClick={() => setPrivateKey('')} title="Clear">×</button>
              </div>
            </div>
            <div className="bg-primary/10 text-primary text-sm rounded-md p-2">
              Note: Ensure port 5062 is open.
            </div>
          </div>
        </div>

        <div>
          <h2 className="font-semibold mb-1">Step 3: Download and run the dial plan script</h2>
          <p className="text-sm text-textSecondary mb-2">
            Select your country to adjust the dial plan script. For more information, check this <a className="text-primary underline" href="https://learn.microsoft.com/microsoftteams/what-are-dial-plans" target="_blank" rel="noreferrer">guide</a>.
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm text-textSecondary mb-1">Country</label>
              <select className="input" value={country} onChange={e => setCountry(e.target.value)}>
                <option value="">— Select —</option>
                {COUNTRY_CODES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm text-textSecondary mb-1">Area code</label>
              <input className="input" value={areaCode} onChange={e => setAreaCode(e.target.value)} />
            </div>
          </div>
          <button className="btn-primary text-sm inline-flex items-center gap-1 mt-2" onClick={downloadDialPlanScript}>
            <Save size={14} /> Generate dial plan script
          </button>
        </div>

        <div>
          <h2 className="font-semibold mb-1">Step 4: Download and run the users script</h2>
          <p className="text-sm text-textSecondary">
            This script configures Teams users with Stratus. It should be executed each time a new user is added or deleted.
          </p>
          <p className="text-sm text-textSecondary mb-2">
            Note: Microsoft Teams might require up to 24 hours to activate configured users.
          </p>
          <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={downloadUsersScript}>
            <Save size={14} /> Generate users script
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────── Hotel Services ──────────────────────

const PMS_TYPES = [
  'Mitel SX2000', 'FCS Connect', 'Tigerlily', 'Opera (Oracle)', 'Hotsoft', 'Comtrol', 'Custom',
];
const NO_ANSWER_DESTS = [
  { v: 'end_call',  label: 'End Call' },
  { v: 'voicemail', label: 'Voicemail' },
  { v: 'extension', label: 'Extension' },
  { v: 'queue',     label: 'Queue' },
  { v: 'external',  label: 'External Number' },
];

function HotelServicesPanel() {
  const qc = useQueryClient();
  const toast = useToast();
  const cfg = useQuery<any>({ queryKey: ['integrations-hotel-services'], queryFn: () => fJson('/api/integrations/hotel-services') });
  const tenants = useQuery<any[]>({ queryKey: ['tenants'], queryFn: () => fJson('/api/tenants') });

  const [enabled,        setEnabled]        = useState(false);
  const [pmsType,        setPmsType]        = useState('Mitel SX2000');
  const [pmsHost,        setPmsHost]        = useState('127.0.0.1');
  const [pmsPort,        setPmsPort]        = useState(15374);
  const [wakeupTimeout,  setWakeupTimeout]  = useState(20);
  const [noAnswerKind,   setNoAnswerKind]   = useState('end_call');
  const [noAnswerValue,  setNoAnswerValue]  = useState('');
  const [roomDepts,      setRoomDepts]      = useState<string[]>([]);

  useEffect(() => {
    const c = cfg.data;
    if (!c) return;
    setEnabled(!!c.enabled);
    setPmsType(c.pms_type || 'Mitel SX2000');
    setPmsHost(c.pms_host || '127.0.0.1');
    setPmsPort(c.pms_port ?? 15374);
    setWakeupTimeout(c.wakeup_timeout ?? 20);
    setNoAnswerKind(c.no_answer?.kind || 'end_call');
    setNoAnswerValue(c.no_answer?.value || '');
    setRoomDepts(Array.isArray(c.room_depts) ? c.room_depts : []);
  }, [cfg.data]);

  const save = useMutation({
    mutationFn: () => fJson('/api/integrations/hotel-services', {
      method: 'PUT',
      body: JSON.stringify({
        enabled, pms_type: pmsType, pms_host: pmsHost, pms_port: pmsPort,
        wakeup_timeout: wakeupTimeout,
        no_answer: { kind: noAnswerKind, value: noAnswerValue },
        room_depts: roomDepts,
      }),
    }),
    onSuccess: () => { toast('success', 'Saved'); qc.invalidateQueries({ queryKey: ['integrations-hotel-services'] }); },
    onError:   (e: any) => toast('error', String(e.message || e)),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold flex-1">Hotel Services</h1>
        <button className="btn-primary text-sm inline-flex items-center gap-1" disabled={save.isPending} onClick={() => save.mutate()}>
          <Save size={14} /> Save
        </button>
      </div>

      <div className="card p-4 space-y-4">
        <label className="inline-flex items-center gap-2 text-sm font-medium">
          <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
          PMS Integration
        </label>
        <p className="text-sm text-textSecondary">
          Installs PMS integration hotel services which reports call costs and performs check in / check out of extensions
        </p>

        <div className="grid grid-cols-2 gap-3">
          <Field label="PMS integration type">
            <select className="input" value={pmsType} onChange={e => setPmsType(e.target.value)}>
              {PMS_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="PMS IP Address">
            <div className="flex gap-1">
              <input className="input flex-1" value={pmsHost} onChange={e => setPmsHost(e.target.value)} placeholder="127.0.0.1" />
              <input className="input w-24" type="number" min={1} max={65535} value={pmsPort}
                     onChange={e => setPmsPort(Number(e.target.value || 0))} />
            </div>
          </Field>
        </div>

        <div>
          <h2 className="font-semibold mb-1">Unanswered wakeup calls</h2>
          <div className="grid grid-cols-[auto_auto_1fr] items-center gap-2">
            <span className="text-sm text-textSecondary">If wake up call is not answered within</span>
            <input className="input w-24" type="number" min={1} max={300} value={wakeupTimeout}
                   onChange={e => setWakeupTimeout(Number(e.target.value || 0))} />
            <span className="text-sm text-textSecondary">seconds, forward call to:</span>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <select className="input" value={noAnswerKind} onChange={e => setNoAnswerKind(e.target.value)}>
              {NO_ANSWER_DESTS.map(d => <option key={d.v} value={d.v}>{d.label}</option>)}
            </select>
            <input className="input" value={noAnswerValue} onChange={e => setNoAnswerValue(e.target.value)}
                   placeholder={noAnswerKind === 'end_call' ? '' : 'extension / number / uuid'}
                   disabled={noAnswerKind === 'end_call'} />
          </div>
        </div>

        <div>
          <h2 className="font-semibold mb-1">Hotel room extension department</h2>
          <select className="input" multiple size={5} value={roomDepts}
                  onChange={e => setRoomDepts(Array.from(e.target.selectedOptions, o => o.value))}>
            {(tenants.data || []).map((t: any) => (
              <option key={t.domain_uuid} value={t.domain_uuid}>{t.domain_description || t.domain_name}</option>
            ))}
          </select>
          <div className="text-xs text-textSecondary mt-1">
            Specify the departments which include the hotel rooms. PMS messages will be sent only to extensions members of these departments
          </div>
        </div>
      </div>
    </div>
  );
}

function TranscriptionPanel() {
  const qc = useQueryClient();
  const toast = useToast();
  const cfg = useQuery<any>({ queryKey: ['integrations-transcription'], queryFn: () => fJson('/api/integrations/transcription') });

  const [provider, setProvider] = useState('deepgram');
  const [apiKey,   setApiKey]   = useState('');
  const [model,    setModel]    = useState('nova-3');
  const [autoForRecordings, setAutoForRecordings] = useState(false);
  const [autoForVoicemail,  setAutoForVoicemail]  = useState(true);
  const [showKey,  setShowKey]  = useState(false);

  useEffect(() => {
    const c = cfg.data;
    if (!c) return;
    setProvider(c.provider || 'deepgram');
    setApiKey(c.api_key || '');
    setModel(c.model || 'nova-3');
    setAutoForRecordings(!!c.auto_for_recordings);
    setAutoForVoicemail(c.auto_for_voicemail !== false);
  }, [cfg.data]);

  const save = useMutation({
    mutationFn: () => fJson('/api/integrations/transcription', {
      method: 'PUT',
      body: JSON.stringify({
        provider, api_key: apiKey, model,
        auto_for_recordings: autoForRecordings,
        auto_for_voicemail: autoForVoicemail,
      }),
    }),
    onSuccess: () => { toast('success', 'Saved'); qc.invalidateQueries({ queryKey: ['integrations-transcription'] }); },
    onError:   (e: any) => toast('error', String(e.message || e)),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold flex-1">Transcription</h1>
        <button className="btn-primary text-sm inline-flex items-center gap-1" disabled={save.isPending} onClick={() => save.mutate()}>
          <Save size={14} /> Save
        </button>
      </div>

      <div className="card p-4 space-y-3">
        <div>
          <label className="block text-sm text-textSecondary mb-1">Provider</label>
          <select className="input" value={provider} onChange={e => setProvider(e.target.value)}>
            <option value="deepgram">Deepgram</option>
            <option value="openai">OpenAI Whisper</option>
            <option value="google">Google Speech-to-Text</option>
            <option value="azure">Azure Speech</option>
          </select>
        </div>
        <div>
          <label className="block text-sm text-textSecondary mb-1">Model</label>
          <input className="input" value={model} onChange={e => setModel(e.target.value)} />
        </div>
        <div>
          <label className="block text-sm text-textSecondary mb-1">API Key</label>
          <div className="relative">
            <input className="input pr-10" type={showKey ? 'text' : 'password'} value={apiKey} onChange={e => setApiKey(e.target.value)} />
            <button className="absolute right-2 top-1/2 -translate-y-1/2 text-textSecondary"
                    onClick={() => setShowKey(s => !s)} title={showKey ? 'Hide' : 'Show'}>
              {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
        </div>
        <div className="flex flex-col gap-2 text-sm">
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={autoForRecordings} onChange={e => setAutoForRecordings(e.target.checked)} />
            Auto-transcribe new recordings
          </label>
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={autoForVoicemail} onChange={e => setAutoForVoicemail(e.target.checked)} />
            Auto-transcribe voicemail
          </label>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────── Data Connectors ──────────────────────

const DB_TYPES = ['PostgreSQL', 'MySQL', 'SQL Server', 'MariaDB', 'Oracle', 'Snowflake'];

function DataConnectorsPanel() {
  const qc = useQueryClient();
  const toast = useToast();
  const cfg = useQuery<any>({ queryKey: ['integrations-data-connectors-cfg'], queryFn: () => fJson('/api/integrations/data-connectors-config') });

  const [dbType,        setDbType]        = useState('PostgreSQL');
  const [host,          setHost]          = useState('');
  const [enableSslVerify, setEnableSslVerify] = useState(false);
  const [port,          setPort]          = useState(5432);
  const [database,      setDatabase]      = useState('');
  const [username,      setUsername]      = useState('');
  const [password,      setPassword]      = useState('');
  const [showPw,        setShowPw]        = useState(false);
  const [includeCdr,        setIncludeCdr]        = useState(true);
  const [includeRecordings, setIncludeRecordings] = useState(true);
  const [includeMetrics,    setIncludeMetrics]    = useState(true);
  const [includeAudio,      setIncludeAudio]      = useState(true);
  const [frequency,     setFrequency]     = useState<'Hourly' | 'Daily' | 'Weekly'>('Hourly');
  const [interval,      setInterval]      = useState(1);
  const [deleteAfter,   setDeleteAfter]   = useState(false);

  useEffect(() => {
    const c = cfg.data;
    if (!c) return;
    setDbType(c.db_type || 'PostgreSQL');
    setHost(c.host || '');
    setEnableSslVerify(!!c.ssl_verify);
    setPort(c.port ?? 5432);
    setDatabase(c.database || '');
    setUsername(c.username || '');
    setPassword(c.password || '');
    setIncludeCdr(c.include_cdr        !== false);
    setIncludeRecordings(c.include_recordings !== false);
    setIncludeMetrics(c.include_metrics    !== false);
    setIncludeAudio(c.include_audio      !== false);
    setFrequency(c.frequency || 'Hourly');
    setInterval(c.interval ?? 1);
    setDeleteAfter(!!c.delete_after_transfer);
  }, [cfg.data]);

  function buildPayload() {
    return {
      db_type: dbType, host, ssl_verify: enableSslVerify, port,
      database, username, password,
      include_cdr: includeCdr, include_recordings: includeRecordings,
      include_metrics: includeMetrics, include_audio: includeAudio,
      frequency, interval, delete_after_transfer: deleteAfter,
    };
  }

  const save = useMutation({
    mutationFn: () => fJson('/api/integrations/data-connectors-config', {
      method: 'PUT', body: JSON.stringify(buildPayload()),
    }),
    onSuccess: () => { toast('success', 'Saved'); qc.invalidateQueries({ queryKey: ['integrations-data-connectors-cfg'] }); },
    onError:   (e: any) => toast('error', String(e.message || e)),
  });

  const test = useMutation({
    mutationFn: () => fJson('/api/integrations/data-connectors-config/test', {
      method: 'POST', body: JSON.stringify(buildPayload()),
    }),
    onSuccess: (r: any) => toast(r?.ok ? 'success' : 'error', r?.message || (r?.ok ? 'Connection OK' : 'Connection failed')),
    onError:   (e: any) => toast('error', String(e.message || e)),
  });

  const runNow = useMutation({
    mutationFn: () => fJson('/api/integrations/data-connectors-config/run', { method: 'POST' }),
    onSuccess: (r: any) => toast('success', `Transferred ${r?.rows ?? 0} rows`),
    onError:   (e: any) => toast('error', String(e.message || e)),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold flex-1">Data Connectors</h1>
        <button className="btn-primary text-sm inline-flex items-center gap-1" disabled={save.isPending} onClick={() => save.mutate()}>
          <Save size={14} /> Save
        </button>
        <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={() => test.mutate()}>
          TEST
        </button>
      </div>

      <div className="card p-4 space-y-4">
        <Field label="Select Database">
          <select className="input" value={dbType} onChange={e => setDbType(e.target.value)}>
            {DB_TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
          <a className="text-xs text-primary underline mt-1 inline-block" href="#">
            Learn how to set up {dbType}
          </a>
        </Field>

        <Field label="Host *">
          <input className="input" value={host} onChange={e => setHost(e.target.value)} />
        </Field>

        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={enableSslVerify} onChange={e => setEnableSslVerify(e.target.checked)} />
          <span>
            <span className="font-medium">Enable SSL Certificate Validation</span>
            <span className="block text-xs text-textSecondary">Ensures the server's certificate is valid and matches the hostname.</span>
          </span>
        </label>

        <Field label="Port *">
          <input className="input" type="number" min={1} max={65535} value={port}
                 onChange={e => setPort(Number(e.target.value || 0))} />
        </Field>

        <Field label="Database name *">
          <input className="input" value={database} onChange={e => setDatabase(e.target.value)} />
        </Field>

        <Field label="Username *">
          <input className="input" value={username} onChange={e => setUsername(e.target.value)} />
        </Field>

        <Field label="Password *">
          <div className="relative">
            <input className="input pr-10" type={showPw ? 'text' : 'password'} value={password}
                   onChange={e => setPassword(e.target.value)} />
            <button className="absolute right-2 top-1/2 -translate-y-1/2 text-textSecondary"
                    onClick={() => setShowPw(s => !s)} title={showPw ? 'Hide' : 'Show'}>
              {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
        </Field>

        <div>
          <h2 className="font-semibold mb-2">Select Data Types to be transferred</h2>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={includeCdr} onChange={e => setIncludeCdr(e.target.checked)} />
              Call History (CDR)
            </label>
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={includeRecordings} onChange={e => setIncludeRecordings(e.target.checked)} />
              Recordings Data
            </label>
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={includeMetrics} onChange={e => setIncludeMetrics(e.target.checked)} />
              System Metrics
            </label>
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={includeAudio} onChange={e => setIncludeAudio(e.target.checked)} />
              Call Audio Metrics
            </label>
          </div>
        </div>

        <div>
          <h2 className="font-semibold mb-2">Schedule Options</h2>
          <div className="flex items-center gap-2 text-sm">
            <span>Frequency</span>
            <select className="input w-32" value={frequency} onChange={e => setFrequency(e.target.value as any)}>
              {(['Hourly', 'Daily', 'Weekly'] as const).map(f => <option key={f}>{f}</option>)}
            </select>
            <span>Every</span>
            <input className="input w-16" type="number" min={1} value={interval}
                   onChange={e => setInterval(Number(e.target.value || 1))} />
            <span>{frequency === 'Hourly' ? 'Hours' : frequency === 'Daily' ? 'Days' : 'Weeks'}</span>
          </div>
          <label className="inline-flex items-center gap-2 text-sm mt-3">
            <input type="checkbox" checked={deleteAfter} onChange={e => setDeleteAfter(e.target.checked)} />
            Delete transferred data from Stratus System after successfully transferred to database.
          </label>
        </div>

        <button className="btn-primary text-sm inline-flex items-center gap-1" disabled={runNow.isPending} onClick={() => runNow.mutate()}>
          <Save size={14} /> Transfer Data Now
        </button>
      </div>
    </div>
  );
}

